/* eslint-disable max-classes-per-file */

import { flushSync } from 'react-dom';
import { createRoot } from 'react-dom/client';
import VenueFilterButton from './venue-filter-button';

class VenueTermsFilterUI {
	constructor(buttonsEl, { onChange, renderOnlyAvailableTerms }) {
		this._handleFilterClick = this._handleFilterClick.bind(this);

		this._onChange = onChange;
		this._renderOnlyAvailableTerms = renderOnlyAvailableTerms ?? true;

		this._buttonsEl = buttonsEl;
		this._activeFilter = null;
	}

	_handleFilterClick(event) {
		this._activeFilter =
			this._activeFilter === event.target.dataset.term
				? null
				: event.target.dataset.term;
		this._onChange(this._activeFilter);
		this._buttonsEl
			.querySelector('.is-active')
			?.classList?.remove?.('is-active');
		event.target.classList.toggle(
			'is-active',
			this._activeFilter === event.target.dataset.term
		);
	}

	renderFilterButtons(terms) {
		if (this._renderOnlyAvailableTerms) {
			const container = document.createElement('div');
			const root = createRoot(container);
			flushSync(() => {
				root.render(
					<>
						{terms.map((term, index) => (
							<VenueFilterButton
								key={index}
								label={term}
								value={term}
							/>
						))}
					</>
				);
			});
			this._buttonsEl.append(...container.children);
		}
		this._listenUIEvents();
	}

	_listenUIEvents() {
		Array.from(this._buttonsEl.children).forEach((buttonEl) =>
			buttonEl.addEventListener('click', this._handleFilterClick)
		);
	}

	clear() {
		this._activeFilter = null;
		this._onChange(this._activeFilter);
		this._buttonsEl
			.querySelector('.is-active')
			?.classList?.remove?.('is-active');
	}
}

class VenueFilter {
	constructor(el) {
		this._scope = document;

		this._filterKeys = [
			'venueCapacity',
			'venueLocation',
			'venueEventStyles',
		];

		this._filterStatus = this._filterKeys.reduce((status, key) => {
			status[key] = null;
			return status;
		}, {});

		this._prepareFilters();
		this._listenWorkerEvents();

		this._clearButtonEl = el.querySelector('.venue-filter__clear-button');

		this._venueCapacityFilter = new VenueTermsFilterUI(
			el.querySelector('.venue-filter__capacity-buttons'),
			{
				renderOnlyAvailableTerms: false,
				onChange: (activeFilter) =>
					this._updateFilter('venueCapacity', activeFilter),
			}
		);

		this._venueLocationFilter = new VenueTermsFilterUI(
			el.querySelector('.venue-filter__location-buttons'),
			{
				onChange: (activeFilter) =>
					this._updateFilter('venueLocation', activeFilter),
			}
		);

		this._venueEventStylesFilter = new VenueTermsFilterUI(
			el.querySelector('.venue-filter__event-styles-buttons'),
			{
				onChange: (activeFilter) =>
					this._updateFilter('venueEventStyles', activeFilter),
			}
		);

		this._listenUIEvents();
	}

	_fillTerms(allTerms) {
		this._filterKeys.forEach((filterName) =>
			this[`_${filterName}Filter`]?.renderFilterButtons(
				allTerms[filterName]
			)
		);
	}

	_updateFilter(key, value) {
		this._filterStatus[key] = value;
		this._clearButtonEl.disabled = !this.isActive();
		this.filterItems();
	}

	_toggleItemsVisibility(itemsVisibility) {
		itemsVisibility.forEach((isVisible, itemIndex) => {
			const venueEl = this._venueItems[itemIndex];
			const linkEl = venueEl.querySelector('a');
			venueEl.classList.toggle('venue-list__item--out', !isVisible);
			venueEl.ariaHidden = !isVisible;
			if (linkEl) linkEl.tabIndex = isVisible ? 0 : -1;
		});
	}

	_listenWorkerEvents() {
		this._worker.addEventListener('message', (event) => {
			const filterEvent = event.data;
			switch (filterEvent.type) {
				case 'READY':
					this._fillTerms(filterEvent.payload);
					break;
				case 'FILTER_RESULTS':
					this._toggleItemsVisibility(filterEvent.payload);
					break;
				default:
					// eslint-disable-next-line no-console
					console.warn(
						`Unhandled filter worker message ${filterEvent.type}`
					);
			}
		});
	}

	_listenUIEvents() {
		this._clearButtonEl.addEventListener('click', () => {
			this.clear();
		});
	}

	_prepareFilters() {
		this._venueItems = Array.from(
			this._scope.querySelectorAll('.venue-list__item')
		);

		this._worker = new Worker(
			new URL('./venue-filter-worker.js', import.meta.url),
			{ type: 'module' }
		);

		const workerData = Object.keys(this._filterStatus).reduce(
			(data, key) => {
				data[key] = this._venueItems.map(
					(itemEl) => itemEl.dataset[key]
				);
				return data;
			},
			{}
		);

		this._worker.postMessage({ type: 'INIT', payload: workerData });
	}

	filterItems() {
		this._worker.postMessage({
			type: 'FILTER',
			payload: this._filterStatus,
		});
	}

	clear() {
		this._filterKeys.forEach((filterName) => {
			this[`_${filterName}Filter`].clear();
		});
	}

	isActive() {
		return this._filterKeys.some((fk) => this._filterStatus[fk] !== null);
	}
}

document.querySelectorAll('.wp-block-occasions-venue-filter').forEach((el) => {
	el._venueFilter = new VenueFilter(el);
	document
		.querySelectorAll('.wp-block-occasions-venue-list')
		.forEach((list) => {
			list.ariaLive = 'polite';
		});
});
